/**
 * App settings context - polls backend so admin changes (maintenance mode, launch mode)
 * propagate to all frontend users without refresh.
 * When an admin updates settings via Early Access ops, clients get new values within POLL_INTERVAL_MS.
 */

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { getAppSettings } from './db-storage';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface AppSettingsState {
  maintenanceMode: boolean;
  betaModeEnabled: boolean;
  earlyAccessModeEnabled: boolean;
  /** Master: MFA feature (Settings UI, enrollment). Default off. */
  mfaEnabled: boolean;
  /** Sub-toggle (only when mfaEnabled): require MFA step after password/OAuth. */
  mfaRequiredAtSignIn: boolean;
  /** Effective login enforcement: mfaEnabled && mfaRequiredAtSignIn */
  mfaEnforcedAtLogin: boolean;
  /** Public marketing: when false, index shows Coming Soon for non-admins. */
  liveProduction: boolean;
  /** Public landing: waitlist splash vs beta landing at `/`. */
  waitlistSplashEnabled: boolean;
  /** Consumer login snapshot generation feature flag. */
  consumerLoginSnapshotEnabled: boolean;
  /** Emergency kill switch for login snapshot AI calls. */
  consumerLoginSnapshotKillSwitch: boolean;
  /** Consumer login snapshot contract version exposed by backend policy. */
  consumerLoginSnapshotSchemaVersion: string;
  /** Snapshot latency warning threshold in milliseconds. */
  consumerLoginSnapshotLatencyWarnMs: number;
  /** Snapshot latency failure threshold in milliseconds. */
  consumerLoginSnapshotLatencyFailMs: number;
  /** Rolling error-rate alert threshold (0-1). */
  consumerLoginSnapshotFailureRateAlertThreshold: number;
  /** Retention period for login snapshot cache in days. */
  consumerLoginSnapshotRetentionDays: number;
  lastFetched: number | null;
}

const defaultState: AppSettingsState = {
  maintenanceMode: false,
  betaModeEnabled: true,
  earlyAccessModeEnabled: true,
  mfaEnabled: false,
  mfaRequiredAtSignIn: false,
  mfaEnforcedAtLogin: false,
  liveProduction: true,
  waitlistSplashEnabled: false,
  consumerLoginSnapshotEnabled: true,
  consumerLoginSnapshotKillSwitch: false,
  consumerLoginSnapshotSchemaVersion: 'consumer_login_snapshot_v1',
  consumerLoginSnapshotLatencyWarnMs: 3500,
  consumerLoginSnapshotLatencyFailMs: 8000,
  consumerLoginSnapshotFailureRateAlertThreshold: 0.2,
  consumerLoginSnapshotRetentionDays: 30,
  lastFetched: null,
};

const AppSettingsContext = createContext<
  AppSettingsState & { refresh: () => Promise<void> }
>({
  ...defaultState,
  refresh: async () => {},
});

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage write failures in restricted browser contexts.
  }
}

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppSettingsState>(() => {
    const local = safeLocalStorageGet('tension_maintenance_mode');
    const mfaEnabled = safeLocalStorageGet('tension_mfa_enabled') === 'true';
    const mfaRequiredAtSignIn = safeLocalStorageGet('tension_mfa_required_for_login') === 'true';
    const liveProduction = safeLocalStorageGet('tension_live_production') !== 'false';
    const earlyAccessModeEnabled =
      safeLocalStorageGet('tension_early_access_mode_enabled') !== 'false';
    const waitlistSplashEnabled = safeLocalStorageGet('tension_waitlist_splash_enabled') === 'true';
    const consumerLoginSnapshotEnabled =
      safeLocalStorageGet('tension_consumer_login_snapshot_enabled') !== 'false';
    const consumerLoginSnapshotKillSwitch =
      safeLocalStorageGet('tension_consumer_login_snapshot_kill_switch') === 'true';
    const consumerLoginSnapshotSchemaVersion =
      safeLocalStorageGet('tension_consumer_login_snapshot_schema_version') ||
      'consumer_login_snapshot_v1';
    const consumerLoginSnapshotLatencyWarnMs = Number(
      safeLocalStorageGet('tension_consumer_login_snapshot_latency_warn_ms') || '3500',
    );
    const consumerLoginSnapshotLatencyFailMs = Number(
      safeLocalStorageGet('tension_consumer_login_snapshot_latency_fail_ms') || '8000',
    );
    const consumerLoginSnapshotFailureRateAlertThreshold = Number(
      safeLocalStorageGet('tension_consumer_login_snapshot_failure_rate_alert_threshold') || '0.2',
    );
    const consumerLoginSnapshotRetentionDays = Number(
      safeLocalStorageGet('tension_consumer_login_snapshot_retention_days') || '30',
    );
    return {
      maintenanceMode: local === 'true',
      betaModeEnabled: safeLocalStorageGet('tension_beta_mode_enabled') !== 'false',
      earlyAccessModeEnabled,
      mfaEnabled,
      mfaRequiredAtSignIn,
      mfaEnforcedAtLogin: mfaEnabled && mfaRequiredAtSignIn,
      liveProduction,
      waitlistSplashEnabled,
      consumerLoginSnapshotEnabled,
      consumerLoginSnapshotKillSwitch,
      consumerLoginSnapshotSchemaVersion,
      consumerLoginSnapshotLatencyWarnMs: Number.isFinite(consumerLoginSnapshotLatencyWarnMs)
        ? consumerLoginSnapshotLatencyWarnMs
        : 3500,
      consumerLoginSnapshotLatencyFailMs: Number.isFinite(consumerLoginSnapshotLatencyFailMs)
        ? consumerLoginSnapshotLatencyFailMs
        : 8000,
      consumerLoginSnapshotFailureRateAlertThreshold:
        Number.isFinite(consumerLoginSnapshotFailureRateAlertThreshold)
          ? consumerLoginSnapshotFailureRateAlertThreshold
          : 0.2,
      consumerLoginSnapshotRetentionDays: Number.isFinite(consumerLoginSnapshotRetentionDays)
        ? consumerLoginSnapshotRetentionDays
        : 30,
      lastFetched: null,
    };
  });

  const refresh = useCallback(async () => {
    try {
      const settings = await getAppSettings();
      const maintenanceMode = settings.tension_maintenance_mode === true;
      const betaModeEnabled = settings.tension_beta_mode_enabled !== false;
      const earlyAccessModeEnabled = settings.tension_early_access_mode_enabled !== false;
      const mfaEnabled = settings.tension_mfa_enabled === true;
      const mfaRequiredAtSignIn = settings.tension_mfa_required_for_login === true;
      const mfaEnforcedAtLogin = mfaEnabled && mfaRequiredAtSignIn;
      const liveProduction = settings.tension_live_production !== false;
      const waitlistSplashEnabled = settings.tension_waitlist_splash_enabled === true;
      const consumerLoginSnapshotEnabled =
        settings.tension_consumer_login_snapshot_enabled !== false;
      const consumerLoginSnapshotKillSwitch =
        settings.tension_consumer_login_snapshot_kill_switch === true;
      const consumerLoginSnapshotSchemaVersion =
        typeof settings.tension_consumer_login_snapshot_schema_version === 'string' &&
        settings.tension_consumer_login_snapshot_schema_version.trim().length > 0
          ? settings.tension_consumer_login_snapshot_schema_version
          : 'consumer_login_snapshot_v1';
      const consumerLoginSnapshotLatencyWarnMsRaw = Number(
        settings.tension_consumer_login_snapshot_latency_warn_ms ?? 3500,
      );
      const consumerLoginSnapshotLatencyFailMsRaw = Number(
        settings.tension_consumer_login_snapshot_latency_fail_ms ?? 8000,
      );
      const consumerLoginSnapshotFailureRateAlertThresholdRaw = Number(
        settings.tension_consumer_login_snapshot_failure_rate_alert_threshold ?? 0.2,
      );
      const consumerLoginSnapshotRetentionDaysRaw = Number(
        settings.tension_consumer_login_snapshot_retention_days ?? 30,
      );
      const consumerLoginSnapshotLatencyWarnMs = Number.isFinite(consumerLoginSnapshotLatencyWarnMsRaw)
        ? consumerLoginSnapshotLatencyWarnMsRaw
        : 3500;
      const consumerLoginSnapshotLatencyFailMs = Number.isFinite(consumerLoginSnapshotLatencyFailMsRaw)
        ? consumerLoginSnapshotLatencyFailMsRaw
        : 8000;
      const consumerLoginSnapshotFailureRateAlertThreshold = Number.isFinite(
        consumerLoginSnapshotFailureRateAlertThresholdRaw,
      )
        ? consumerLoginSnapshotFailureRateAlertThresholdRaw
        : 0.2;
      const consumerLoginSnapshotRetentionDays = Number.isFinite(consumerLoginSnapshotRetentionDaysRaw)
        ? consumerLoginSnapshotRetentionDaysRaw
        : 30;
      safeLocalStorageSet('tension_maintenance_mode', String(maintenanceMode));
      safeLocalStorageSet('tension_beta_mode_enabled', String(betaModeEnabled));
      safeLocalStorageSet('tension_early_access_mode_enabled', String(earlyAccessModeEnabled));
      safeLocalStorageSet('tension_mfa_enabled', String(mfaEnabled));
      safeLocalStorageSet('tension_mfa_required_for_login', String(mfaRequiredAtSignIn));
      safeLocalStorageSet('tension_live_production', String(liveProduction));
      safeLocalStorageSet('tension_waitlist_splash_enabled', String(waitlistSplashEnabled));
      safeLocalStorageSet(
        'tension_consumer_login_snapshot_enabled',
        String(consumerLoginSnapshotEnabled),
      );
      safeLocalStorageSet(
        'tension_consumer_login_snapshot_kill_switch',
        String(consumerLoginSnapshotKillSwitch),
      );
      safeLocalStorageSet(
        'tension_consumer_login_snapshot_schema_version',
        consumerLoginSnapshotSchemaVersion,
      );
      safeLocalStorageSet(
        'tension_consumer_login_snapshot_latency_warn_ms',
        String(consumerLoginSnapshotLatencyWarnMs),
      );
      safeLocalStorageSet(
        'tension_consumer_login_snapshot_latency_fail_ms',
        String(consumerLoginSnapshotLatencyFailMs),
      );
      safeLocalStorageSet(
        'tension_consumer_login_snapshot_failure_rate_alert_threshold',
        String(consumerLoginSnapshotFailureRateAlertThreshold),
      );
      safeLocalStorageSet(
        'tension_consumer_login_snapshot_retention_days',
        String(consumerLoginSnapshotRetentionDays),
      );
      setState({
        maintenanceMode,
        betaModeEnabled,
        earlyAccessModeEnabled,
        mfaEnabled,
        mfaRequiredAtSignIn,
        mfaEnforcedAtLogin,
        liveProduction,
        waitlistSplashEnabled,
        consumerLoginSnapshotEnabled,
        consumerLoginSnapshotKillSwitch,
        consumerLoginSnapshotSchemaVersion,
        consumerLoginSnapshotLatencyWarnMs,
        consumerLoginSnapshotLatencyFailMs,
        consumerLoginSnapshotFailureRateAlertThreshold,
        consumerLoginSnapshotRetentionDays,
        lastFetched: Date.now(),
      });
    } catch {
      // Keep previous state
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  return (
    <AppSettingsContext.Provider value={{ ...state, refresh }}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export function useAppSettings() {
  return useContext(AppSettingsContext);
}
